import { getReturnPeriodLabel, Hazard, ResponseSuccessWithMetadata, ResultMetadata } from "@/features/pe_aldd";
import { autoAnalysedResilienceTypeValues, type AdaptationType } from "@/types/adaptation";
import { AnomalyManagamentSupportedHazards, DefenceSupportedHazards } from "@/utils/hazards";

export const isAnomalyManagementApplied = (anomalyManagementRp: AnomalyManagamentSupportedHazards) =>
  !!Object.values(anomalyManagementRp || []).find((am) => am !== null);

export const isDefenceApplied = (defences: DefenceSupportedHazards) =>
  !!Object.values(defences || []).find((defence) => defence !== "undefended");

export const isPointAnomalyManagamentApplied = (metadata: ResultMetadata) => {
  return !!Object.values(metadata.incorporated_hazards).find((h) => {
    return metadata[h]?.protections?.anomaly_management?.rp != null;
  });
};

export const isPointDefenceApplied = (metadata: ResultMetadata) => {
  return !!Object.values(metadata.incorporated_hazards as Hazard[]).find((h) => {
    return metadata[h]?.protections?.defences !== null;
  });
};

// defence and anomaly can be applied
export const isProtectionsApplicable = (hazard: Hazard) =>
  (["flood_riverine", "flood_surfacewater", "coastal_inundation", "cyclone_storm_surge"] as Hazard[]).includes(hazard);

type ThresholdOutput = {
  value: string | number | undefined;
  source: string;
};

export const formatReturnPeriod = (value: ThresholdOutput["value"]) =>
  typeof value === "number" ? getReturnPeriodLabel(value, false) : value;

export const formatThreshold = (returnPeriod?: ThresholdOutput, measure?: ThresholdOutput, unit = "") => {
  if (typeof measure?.value !== "number") return null;
  return [
    typeof returnPeriod?.value === "number" ? `1 in ${returnPeriod.value}` : null,
    measure?.value != null ? `${measure.value}${unit}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const USER_SUPPLIED_SOURCE = "user-supplied";
export const SYSTEM_ASSIGNED_SOURCE = "system-assigned";

export const getThresholdPairSource = (...sources: Array<string | undefined>) =>
  sources.includes(USER_SUPPLIED_SOURCE) ? USER_SUPPLIED_SOURCE : (sources.find(Boolean) ?? "");

const getThresholdSource = (input: number | null, result: number | undefined | null) =>
  typeof result === "number" ? (result === input ? USER_SUPPLIED_SOURCE : SYSTEM_ASSIGNED_SOURCE) : "";

type InputThresholds = NonNullable<ResponseSuccessWithMetadata["metadata"]["thresholds"]>;

const getInputReturnPeriod = (
  thresholds: InputThresholds["heat"] | InputThresholds["wind"] | InputThresholds["cyclone_wind"]
) => (thresholds && "return_period" in thresholds ? thresholds.return_period : null);

const isAutoResilience = (adaptationType?: AdaptationType) =>
  autoAnalysedResilienceTypeValues.some((type) => type === adaptationType);

/** Protections on an auto pathway always come from autoAdaptationInputSettings, never the user. */
export const getProtectionSource = (value: string | null | undefined, adaptationType?: AdaptationType) =>
  value != null && !isAutoResilience(adaptationType) ? USER_SUPPLIED_SOURCE : SYSTEM_ASSIGNED_SOURCE;

export const getThresholdsFromMetadata = (
  analysisInputMetadata: ResponseSuccessWithMetadata["metadata"],
  analysisResultMetadata: ResultMetadata,
  adaptationType?: AdaptationType
) => {
  const getReturnPeriodSource = (input: number | null, result: number | undefined) => {
    // Return periods on an auto pathway always come from autoAdaptationInputSettings, never the user.
    if (isAutoResilience(adaptationType)) {
      return SYSTEM_ASSIGNED_SOURCE;
    }

    // A result that omits the return period leaves only the input to fall back on, which is input by user
    if (result == null) {
      return input == null ? "" : USER_SUPPLIED_SOURCE;
    }

    return getThresholdSource(input, result);
  };

  // thresholds from analysis results
  const heatResultThresholds = analysisResultMetadata.heat?.thresholds;
  const windResultThresholds = analysisResultMetadata.wind?.thresholds;
  const cycloneWindResultThresholds = analysisResultMetadata.cyclone_wind?.thresholds;

  // thresholds from analysis inputs
  const {
    heat: heatInputThresholds,
    wind: windInputThresholds,
    cyclone_wind: cycloneWindInputThresholds,
  } = analysisInputMetadata?.thresholds || {};

  const userSuppliedThresholds = {
    windSpeed:
      windInputThresholds && "wind_speed" in windInputThresholds && typeof windInputThresholds.wind_speed === "number"
        ? windInputThresholds.wind_speed
        : null,
    cycloneWindSpeed:
      cycloneWindInputThresholds &&
      "wind_speed" in cycloneWindInputThresholds &&
      typeof cycloneWindInputThresholds.wind_speed === "number"
        ? cycloneWindInputThresholds.wind_speed
        : null,
    heatTemperature:
      heatInputThresholds && "temperature" in heatInputThresholds ? heatInputThresholds.temperature : null,
    windReturnPeriod: getInputReturnPeriod(windInputThresholds),
    cycloneWindReturnPeriod: getInputReturnPeriod(cycloneWindInputThresholds),
    heatReturnPeriod: getInputReturnPeriod(heatInputThresholds),
  };

  const thresholds: Record<string, ThresholdOutput> = {
    // extreme_wind
    windSpeed: {
      value: windResultThresholds?.wind_speed,
      source: getThresholdSource(userSuppliedThresholds.windSpeed, windResultThresholds?.wind_speed),
    },
    windReturnPeriod: {
      value: windResultThresholds?.return_period ?? userSuppliedThresholds.windReturnPeriod ?? undefined,
      source: getReturnPeriodSource(userSuppliedThresholds.windReturnPeriod, windResultThresholds?.return_period),
    },

    // cyclone_storm_surge
    cycloneWindSpeed: {
      value: cycloneWindResultThresholds?.wind_speed,
      source: getThresholdSource(userSuppliedThresholds.cycloneWindSpeed, cycloneWindResultThresholds?.wind_speed),
    },
    cycloneWindReturnPeriod: {
      value: cycloneWindResultThresholds?.return_period ?? userSuppliedThresholds.cycloneWindReturnPeriod ?? undefined,
      source: getReturnPeriodSource(
        userSuppliedThresholds.cycloneWindReturnPeriod,
        cycloneWindResultThresholds?.return_period
      ),
    },

    // extreme_heat
    heatTemperature: {
      value: heatResultThresholds?.temperature,
      source: getThresholdSource(userSuppliedThresholds.heatTemperature, heatResultThresholds?.temperature),
    },
    heatReturnPeriod: {
      value: heatResultThresholds?.return_period ?? userSuppliedThresholds.heatReturnPeriod ?? undefined,
      source: getReturnPeriodSource(userSuppliedThresholds.heatReturnPeriod, heatResultThresholds?.return_period),
    },
  };

  return thresholds;
};
